import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ShopifyProduct } from '../../types/shopify';
import ShopifyService from '../../services/shopify';
import './HomePage.scss';

// Типы для Vimeo Player API
declare global {
  interface Window {
    Vimeo: any;
  }
}

const HomePage: React.FC = () => {
  const [products, setProducts] = useState<ShopifyProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [isMuted, setIsMuted] = useState(true);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const playerRef = useRef<any>(null);
  const vimeoScriptLoaded = useRef(false);

  useEffect(() => {
    const loadProducts = async () => {
      try {
        setLoading(true);
        // Загружаем все продукты и ищем Classic Benjamin и Vesper
        const allProducts = await ShopifyService.getProducts(50);
        
        // Ищем продукты по названию (case-insensitive)
        const classicBenjamin = allProducts.find(p => 
          p.title.toLowerCase().includes('classic benjamin') || 
          p.handle.toLowerCase().includes('classic-benjamin')
        );
        
        const vesper = allProducts.find(p => 
          p.title.toLowerCase().includes('vesper') || 
          p.handle.toLowerCase().includes('vesper')
        );

        const featuredProducts: ShopifyProduct[] = [];
        if (classicBenjamin) featuredProducts.push(classicBenjamin);
        if (vesper) featuredProducts.push(vesper);

        // Если не нашли по названию, берем первые два продукта
        if (featuredProducts.length < 2) {
          const remaining = allProducts.filter(p => 
            !featuredProducts.some(fp => fp.id === p.id)
          );
          featuredProducts.push(...remaining.slice(0, 2 - featuredProducts.length));
        }

        setProducts(featuredProducts);
      } catch (error) {
        console.error('Failed to load products:', error);
      } finally {
        setLoading(false);
      }
    };

    loadProducts();
  }, []);

  // Загружаем Vimeo Player API и инициализируем плеер
  useEffect(() => {
    if (!vimeoScriptLoaded.current) {
      const script = document.createElement('script');
      script.src = 'https://player.vimeo.com/api/player.js';
      script.async = true;
      script.onload = () => {
        if (iframeRef.current && window.Vimeo) {
          playerRef.current = new window.Vimeo.Player(iframeRef.current);
          // Устанавливаем начальное состояние (muted)
          playerRef.current.setVolume(0);
        }
      };
      document.body.appendChild(script);
      vimeoScriptLoaded.current = true;
    } else if (iframeRef.current && window.Vimeo && !playerRef.current) {
      // Если скрипт уже загружен, но плеер не инициализирован
      playerRef.current = new window.Vimeo.Player(iframeRef.current);
      playerRef.current.setVolume(0);
    }
  }, []);

  const toggleMute = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (playerRef.current) {
      try {
        const volume = await playerRef.current.getVolume();
        if (volume > 0) {
          await playerRef.current.setVolume(0);
          setIsMuted(true);
        } else {
          await playerRef.current.setVolume(1);
          setIsMuted(false);
        }
      } catch (error) {
        console.error('Error toggling mute:', error);
      }
    }
  };

  const getMainImage = (product: ShopifyProduct) => {
    // Ищем превью-изображение (фото человека в футболке)
    if (product.images && product.images.length > 0) {
      // 1. Сначала ищем по altText на наличие ключевых слов
      const previewImageByAlt = product.images.find(img => {
        const altText = (img.altText || '').toLowerCase();
        return altText.includes('preview') || 
               altText.includes('hero') || 
               altText.includes('model') || 
               altText.includes('worn') ||
               altText.includes('preview-image');
      });
      
      if (previewImageByAlt) {
        return previewImageByAlt.url;
      }
      
      // 2. Если не найдено по altText, используем последнее изображение
      // (часто превью добавляется последним)
      const lastImage = product.images[product.images.length - 1];
      if (lastImage) {
        return lastImage.url;
      }
      
      // 3. Если только одно изображение, используем его
      return product.images[0].url;
    }
    
    // Fallback - возвращаем null, чтобы использовать CSS плейсхолдер
    return null;
  };

  return (
    <div className="home-page">
      {/* Two Product Blocks */}
      <section className="home-page__image-blocks">
        {loading ? (
          <>
            <div className="home-page__image-block">
              <div className="home-page__image-container home-page__image-container--placeholder">
                <div className="home-page__image-overlay">
                  <span>Loading...</span>
                </div>
              </div>
            </div>
            <div className="home-page__image-block">
              <div className="home-page__image-container home-page__image-container--placeholder">
                <div className="home-page__image-overlay">
                  <span>Loading...</span>
                </div>
              </div>
            </div>
          </>
        ) : (
          products.map((product, index) => {
            const imageUrl = getMainImage(product);
            return (
              <div key={product.id} className="home-page__image-block">
                <Link to={`/products/${product.handle}`} className="home-page__image-link">
                  <div 
                    className={`home-page__image-container ${!imageUrl ? 'home-page__image-container--placeholder' : ''}`}
                    style={imageUrl ? {backgroundImage: `url(${imageUrl})`} : {}}
                  >
                    <div className="home-page__image-overlay">
                      <span>{product.title}</span>
                    </div>
                  </div>
                </Link>
              </div>
            );
          })
        )}
      </section>


      {/* Hero Video Section */}
      <section className="home-page__hero">
        <Link to="/archive" className="home-page__hero-link">
          <div className="home-page__hero-media">
            <div className="home-page__video-wrapper">
              <iframe
                ref={iframeRef}
                src="https://player.vimeo.com/video/1153026972?badge=0&autopause=0&player_id=0&app_id=58479&autoplay=1&loop=1&muted=1&background=1&controls=0"
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                className="home-page__hero-video"
                title="Main Homepage Hero"
              />
            </div>
          </div>
          <div className="home-page__hero-text">
            <div className="home-page__hero-button">
              <span className="home-page__season-link">ARCHIVE</span>
            </div>
          </div>
          <div className="home-page__speaker-container">
            <button 
              className="home-page__speaker-button"
              onClick={toggleMute}
              aria-label={isMuted ? 'Unmute video' : 'Mute video'}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M11 5L6 9H2V15H6L11 19V5Z" stroke="white" strokeWidth="2" />
                {isMuted ? (
                  <path d="M23 9L17 15M17 9L23 15" stroke="white" strokeWidth="2" strokeLinecap="round" />
                ) : (
                  <path d="M19.07 4.92999C20.9447 6.80527 21.9979 9.34835 21.9979 12C21.9979 14.6516 20.9447 17.1947 19.07 19.07M15.54 8.45999C16.4774 9.39763 17.0039 10.6692 17.0039 11.995C17.0039 13.3208 16.4774 14.5924 15.54 15.53" stroke="white" strokeWidth="2" />
                )}
              </svg>
            </button>
          </div>
        </Link>
      </section>
    </div>
  );
};

export default HomePage;